import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationCenter from '../notifications/NotificationCenter';
import { ThemeToggle } from '../../hooks/useTheme';
import { useKeyboardShortcuts, KeyboardShortcutsHelp } from '../../hooks/useKeyboardShortcuts';
import {
  ChartPieIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ShoppingBagIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CubeIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  StarIcon,
  PresentationChartLineIcon,
  ChartBarSquareIcon,
  BoltIcon,
  FunnelIcon,
  GiftIcon,
  TagIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  UserGroupIcon,
  TrophyIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    analytics: true,
    products: true,
    orders: false,
    finance: false,
    customers: false,
    fieldAgents: false
  });

  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize keyboard shortcuts
  useKeyboardShortcuts();

  const navigationSections = [
    {
      type: 'single',
      name: 'דשבורד ראשי',
      href: '/admin',
      icon: ChartPieIcon
    },
    {
      type: 'group',
      id: 'analytics',
      name: 'אנליטיקה ודוחות',
      icon: ChartBarIcon,
      items: [
        { name: 'ניתוח עסקי', href: '/admin/business-analytics', icon: ChartBarIcon },
        { name: 'דוחות עסקיים', href: '/admin/profitability', icon: PresentationChartLineIcon },
        { name: 'אנליטיקה בזמן אמת', href: '/admin/real-time-analytics', icon: BoltIcon },
        { name: 'משפכי המרה', href: '/admin/conversion-funnels', icon: FunnelIcon },
        { name: 'ניהול אנליטיקה', href: '/admin/analytics-management', icon: ChartBarSquareIcon },
        { name: 'בדיקת אנליטיקה', href: '/admin/analytics-test', icon: ChartBarSquareIcon },
      ]
    },
    {
      type: 'group',
      id: 'products',
      name: 'מוצרים וקטלוג',
      icon: CubeIcon,
      items: [
        { name: 'מוצרים', href: '/admin/products', icon: CubeIcon },
        { name: 'קטגוריות', href: '/admin/categories', icon: TagIcon },
        { name: 'ברנדים', href: '/admin/brands', icon: BuildingOfficeIcon },
        { name: 'ניהול מלאי', href: '/admin/inventory', icon: CubeIcon },
        { name: 'דוחות בלאי', href: '/admin/waste-reports', icon: ExclamationTriangleIcon },
        { name: 'אנליטיקת בלאי', href: '/admin/waste-analytics', icon: ChartBarIcon },
        { name: 'ניהול החזרות', href: '/admin/returns', icon: ExclamationTriangleIcon },
        { name: 'אופטימיזציית מלאי', href: '/admin/inventory-optimization', icon: LightBulbIcon },
        { name: 'חבילות', href: '/admin/bundles', icon: GiftIcon },
      ]
    },
    {
      type: 'group',
      id: 'procurement',
      name: 'רכש וספקים',
      icon: ClipboardDocumentListIcon,
      items: [
        { name: 'ספקים', href: '/admin/suppliers', icon: UsersIcon },
        { name: 'הזמנות רכש', href: '/admin/purchase-orders', icon: ShoppingBagIcon },
        { name: 'קבלת סחורה', href: '/admin/goods-receipts', icon: ClipboardDocumentListIcon },
      ]
    },
    {
      type: 'group',
      id: 'orders',
      name: 'הזמנות',
      icon: ShoppingBagIcon,
      items: [
        { name: 'הזמנות', href: '/admin/orders', icon: ShoppingBagIcon },
        { name: 'הזמנות סוכנים', href: '/admin/agent-orders', icon: UserGroupIcon },
      ]
    },
    {
      type: 'group',
      id: 'finance',
      name: 'ניהול כספים',
      icon: BanknotesIcon,
      items: [
        { name: 'חובות חברות', href: '/admin/companies-debt', icon: CurrencyDollarIcon },
        { name: 'דוחות גבייה', href: '/admin/payment-reports', icon: ChartBarIcon },
        { name: 'ניהול צ\'קים', href: '/admin/checks', icon: DocumentTextIcon },
      ]
    },
    {
      type: 'group',
      id: 'customers',
      name: 'לקוחות ועסקים',
      icon: UsersIcon,
      items: [
        { name: 'לקוחות', href: '/admin/customers', icon: UsersIcon },
        { name: 'חברות', href: '/admin/companies', icon: BuildingOfficeIcon },
        { name: 'חוזים', href: '/admin/contracts', icon: DocumentTextIcon },
      ]
    },
    {
      type: 'group',
      id: 'fieldAgents',
      name: 'סוכני שטח',
      icon: UserGroupIcon,
      items: [
        { name: 'ניהול סוכנים', href: '/admin/field-agents', icon: UserGroupIcon },
        { name: 'יעדי מכירה', href: '/admin/sales-targets', icon: TrophyIcon },
        { name: 'מעקב ביצועים', href: '/admin/agent-performance', icon: ChartBarIcon },
      ]
    },
    {
      type: 'single',
      name: 'ביקורות',
      href: '/admin/reviews',
      icon: StarIcon
    },
    {
      type: 'single',
      name: 'תכונות המערכת',
      href: '/admin/features',
      icon: SparklesIcon
    },
    {
      type: 'single',
      name: 'הגדרות',
      href: '/admin/settings',
      icon: CogIcon
    },
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Listen for custom events from keyboard shortcuts
  useEffect(() => {
    const handleShowShortcutsHelp = () => setShowShortcutsHelp(true);
    const handleShowGlobalSearch = () => setShowGlobalSearch(true);
    const handleCloseAllModals = () => {
      setShowShortcutsHelp(false);
      setShowGlobalSearch(false);
      setSidebarOpen(false);
    };

    document.addEventListener('showShortcutsHelp', handleShowShortcutsHelp);
    document.addEventListener('showGlobalSearch', handleShowGlobalSearch);
    document.addEventListener('closeAllModals', handleCloseAllModals);

    return () => {
      document.removeEventListener('showShortcutsHelp', handleShowShortcutsHelp);
      document.removeEventListener('showGlobalSearch', handleShowGlobalSearch);
      document.removeEventListener('closeAllModals', handleCloseAllModals);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleGlobalSearch = (e) => {
    e.preventDefault();
    if (globalSearchQuery.trim()) {
      // Implement global search logic here
      // For now, just close the search modal
      setShowGlobalSearch(false);
      setGlobalSearchQuery('');
    }
  };

  const isCurrentPage = (href) => {
    if (href === '/admin' && location.pathname === '/admin') {
      return true;
    }
    return location.pathname.startsWith(href) && href !== '/admin';
  };

  const isGroupActive = (items) => {
    return items.some(item => isCurrentPage(item.href));
  };

  const renderNavigationItem = (section, isMobile = false) => {
    if (section.type === 'single') {
      const Icon = section.icon;
      return (
          <Link
          key={section.name}
          to={section.href}
          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isCurrentPage(section.href)
            ? 'bg-gray-100 text-gray-900 border-r-2 border-gray-900'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          onClick={() => isMobile && setSidebarOpen(false)}
        >
          <Icon
            className={`ml-3 flex-shrink-0 h-5 w-5 ${isCurrentPage(section.href) ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-600'
              }`}
          />
          {section.name}
        </Link>
      );
    }

    if (section.type === 'group') {
      const Icon = section.icon;
      const isExpanded = expandedSections[section.id];
      const hasActiveItem = isGroupActive(section.items);

      return (
        <div key={section.id} className="space-y-1">
          <button
            onClick={() => toggleSection(section.id)}
            className={`w-full group flex items-center justify-between px-3 py-2 text-sm font-semibold rounded-md transition-colors ${hasActiveItem
              ? 'bg-gray-50 text-gray-900'
              : 'text-gray-700 hover:bg-gray-50'
              }`}
          >
            <div className="flex items-center">
              <Icon
                className={`ml-3 flex-shrink-0 h-5 w-5 ${hasActiveItem ? 'text-gray-700' : 'text-gray-400 group-hover:text-gray-600'
                  }`}
              />
              <span>{section.name}</span>
            </div>
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronLeftIcon className="h-4 w-4 text-gray-500" />
            )}
          </button>

          {isExpanded && (
            <div className="pr-6 space-y-0.5">
              {section.items.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${isCurrentPage(item.href)
                      ? 'bg-gray-100 text-gray-900 border-r-2 border-gray-700'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    onClick={() => isMobile && setSidebarOpen(false)}
                  >
                    <ItemIcon
                      className={`ml-2 flex-shrink-0 h-3.5 w-3.5 ${isCurrentPage(item.href) ? 'text-gray-700' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
      )}

      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 right-0 flex w-64 flex-col z-50 lg:hidden transform ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        } transition-transform duration-300 ease-in-out`}>
        <div className="flex flex-col flex-grow bg-white border-l border-gray-200 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4">
            <h2 className="text-lg font-semibold text-gray-900">ממשק ניהול</h2>
            <button
              className="mr-auto text-gray-400 hover:text-gray-600"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-5 flex-grow flex flex-col">
            <nav className="flex-1 px-2 space-y-1">
              {navigationSections.map((section) => renderNavigationItem(section, true))}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:right-0 z-30">
        <div className="flex flex-col flex-grow bg-white border-l border-gray-200 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4">
            <h2 className="text-lg font-semibold text-gray-900">ממשק ניהול</h2>
          </div>
          <div className="mt-5 flex-grow flex flex-col">
            <nav className="flex-1 px-2 space-y-1.5">
              {navigationSections.map((section) => renderNavigationItem(section, false))}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="lg:pr-64">
        {/* Top navigation */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                {/* Mobile menu button */}
                <button
                  type="button"
                  className="lg:hidden text-gray-400 hover:text-gray-600"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Bars3Icon className="h-6 w-6" />
                </button>

                {/* Breadcrumb */}
                <div className="mr-4 lg:mr-0">
                  <h1 className="text-xl font-semibold text-gray-900">
                    {(() => {
                      for (const section of navigationSections) {
                        if (section.type === 'single' && isCurrentPage(section.href)) {
                          return section.name;
                        }
                        if (section.type === 'group') {
                          const activeItem = section.items.find(item => isCurrentPage(item.href));
                          if (activeItem) return activeItem.name;
                        }
                      }
                      return 'ממשק ניהול';
                    })()}
                  </h1>
                </div>
              </div>

              {/* Top Navigation Tools */}
              <div className="flex items-center space-x-4">
                {/* Global Search */}
                <button
                  onClick={() => setShowGlobalSearch(true)}
                  className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full"
                  title="חיפוש גלובלי (Ctrl+K)"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>

                {/* Notifications */}
                <NotificationCenter />

                {/* Theme Toggle */}
                <ThemeToggle />

                {/* Help & Shortcuts */}
                <button
                  onClick={() => setShowShortcutsHelp(true)}
                  className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full"
                  title="עזרה וקיצורי מקלדת (?)"
                >
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </button>

                <div className="border-r border-gray-200 h-8 hidden sm:block" />

                {/* User Profile */}
                <div className="flex items-center space-x-2">
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                </div>

                <div className="border-r border-gray-200 h-8 hidden sm:block" />

                <button
                  onClick={handleLogout}
                  className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 ml-1" />
                  <span className="hidden sm:inline">יציאה</span>
                </button>

                <Link
                  to="/"
                  className="text-sm text-primary-600 hover:text-primary-700"
                  target="_blank"
                >
                  צפה באתר
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Global Search Modal */}
      {showGlobalSearch && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen pt-16 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowGlobalSearch(false)}
            />

            {/* Search Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">חיפוש גלובלי</h3>
                  <p className="text-sm text-gray-500 mt-1">חפש מוצרים, הזמנות, לקוחות או חברות</p>
                </div>

                <form onSubmit={handleGlobalSearch}>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      value={globalSearchQuery}
                      onChange={(e) => setGlobalSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="הקלד כדי לחפש..."
                      autoFocus
                      data-global-search
                    />
                  </div>

                  <div className="mt-4 text-xs text-gray-500">
                    <p>עצות: השתמש ב-Ctrl+K לפתיחה מהירה, Esc לסגירה</p>
                  </div>
                </form>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleGlobalSearch}
                >
                  חפש
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowGlobalSearch(false)}
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
      />
    </div>
  );
};

export default AdminLayout;
